import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Every `--animate-*` token in appTailwind.css's `@theme` block. A
 * component-owned animation is a plain class named outside `animate-`
 * (accordion.css says why), so it is not listed here.
 *
 * tailwind-merge can't infer this namespace: unlisted, `animate-fade-in` is
 * unclassified and a later `animate-none` can't turn it off. The spec holds
 * this list to appTailwind.css and every css file under `ui/`.
 *
 * No sizes or shadows on purpose: the set uses Tailwind's own `text-sm` and
 * `shadow-md`. A custom `--text-*` or `--shadow-*` token would need listing,
 * since tailwind-merge reads an unknown `text-` as a colour and
 * `text-body2 text-error` would silently drop the size.
 */
export const UI_ANIMATIONS = ['fade-in', 'grow-in'] as const;

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      animate: [{ animate: [...UI_ANIMATIONS] }],
    },
  },
});

/**
 * Joins Tailwind classes and lets the last one win.
 *
 * `clsx` drops falsy branches; the merge resolves conflicts like `p-2` vs
 * `px-8`, which otherwise go to whichever the stylesheet emits last. That lets
 * `<Button className='px-8'>` override the variant's padding.
 *
 * The marketing sites use their own `cx` (no merge): they pass no className
 * overrides.
 */
export const cn = (...classNames: ClassValue[]): string =>
  twMerge(clsx(classNames));
